import * as React from 'react'
import { useMemo, useState, useCallback, useEffect } from 'react'

import Box from '@mui/material/Box'
import useTheme from '@mui/material/styles/useTheme'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import AlertTitle from '@mui/material/AlertTitle'
import FormHelperText from '@mui/material/FormHelperText'

import { Chevron } from '@sales-center/components'
import { useFormikContext, useField } from 'formik'
import { IAddOpportunityFormModel } from '@user/components/Opportunities/AddOpportunityPage/typings'
import {
  ProgressWidget,
  IProgressItem,
  IProgressWidgetColors,
} from './ProgressWidget'
import { ClosedWonLostMenu } from './ClosedWonLostMenu/ClosedWonLostMenu'

import { useSelectOppStages } from '@user/store/salesFunnel'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { IOpportunityIsCloseableResponse } from '@user/store/opportunity'
import {
  closedWonOpportunityStageName,
  closedLostOpportunityStageName,
} from '@sales-center/common'

type Props = {
  disableOptions: boolean
  isCloseableDetails?: IOpportunityIsCloseableResponse
  name: string
  onClosedWonChange: (isClosedWon: boolean, isClosedLost: boolean) => void
}

export function OpportunityStageMeter(props: Props) {
  const { isCloseableDetails, name, onClosedWonChange } = props
  const [isSelectedCloseWon, setIsSelectedCloseWon] = useState(false)
  const theme = useTheme()

  const isTidalEnabled = useTidalThemeActive()

  const colors: IProgressWidgetColors = {
    completeColors: {
      backgroundColor: isTidalEnabled
        ? theme.palette.secondary.main
        : '#19305a',
      backgroundColorHover: isTidalEnabled
        ? theme.palette.secondary.light
        : '#122240',
      textColor: isTidalEnabled
        ? theme.palette.text.primary
        : theme.palette.common.white,
      textColorHover: isTidalEnabled
        ? theme.palette.text.primary
        : theme.palette.common.white,
      strokeColor: '#19305a',
      strokeColorHover: '#122240',
    },
    incompleteColors: {
      backgroundColor: isTidalEnabled ? '#F5F5F5' : '#ebeff3',
      backgroundColorHover: '#f7f7f7',
      textColor: isTidalEnabled ? theme.palette.text.primary : '#19305a',
      textColorHover: isTidalEnabled ? theme.palette.text.primary : '#122240',
      strokeColor: '#375ba3',
      strokeColorHover: '#375ba3',
    },
  }

  const {
    setFieldValue,
    setValues,
    values: {
      salesFunnelId,
      opportunityStageId,
      opportunityStageClosedLostIdTemp,
    },
  } = useFormikContext<IAddOpportunityFormModel>()

  const [_, { error, touched }] = useField({
    name,
    validate: (value) => {
      if (!value) {
        return 'Required'
      }
    },
  })

  const showError = Boolean(error && touched)
  const helperText = showError ? error : ''

  const [salesFunnelStages, closedWonStageId, closedLostStageId] =
    useSelectOppStages(parseInt(salesFunnelId))

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const wonLostMenuOpen = Boolean(anchorEl)

  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(
    null,
  )

  const notCloseableMessage =
    !isCloseableDetails || isCloseableDetails.closeable
      ? ''
      : 'Closing requirements have not been met. ' +
        isCloseableDetails.requirements.map((r) => r.reason).join(' ')

  const items = useMemo(() => {
    let stageComplete: boolean = opportunityStageId !== ''
    const tempItems: IProgressItem[] = []

    // non-closed
    salesFunnelStages
      .filter(
        (s) =>
          s.name !== closedWonOpportunityStageName &&
          s.name !== closedLostOpportunityStageName,
      )
      .map((stage) => {
        tempItems.push({
          isComplete: stageComplete,
          text: stage.name,
          value: stage.id,
          dataTestId: `opportunityStage-${stage.id}`,
        })
        if (stage.id === parseInt(opportunityStageId)) {
          stageComplete = false
        }
      })

    const isClosed: boolean =
      parseInt(opportunityStageId) === closedWonStageId ||
      parseInt(opportunityStageId) === closedLostStageId

    tempItems.push({
      isComplete: isClosed,
      text: 'Closed Won / Closed Lost',
      value: `${closedWonStageId}|${closedLostStageId}`,
      endAdornment: (open) => (
        <Chevron
          open={open}
          color="#a9aaab"
          dataTestId="closedLostWonChevron"
        />
      ),
      onClick: (e) => setAnchorEl(e.currentTarget),
      dataTestId: 'closedLostWonTempBtn',
    })

    return tempItems
  }, [
    closedLostStageId,
    closedWonStageId,
    opportunityStageId,
    salesFunnelStages,
  ])

  useEffect(() => {
    if (!opportunityStageId && salesFunnelStages?.length == 1) {
      setFieldValue(name, salesFunnelStages[0].id)
    }
  }, [opportunityStageId, setFieldValue])

  const lastCompleteIndex = useMemo(
    () =>
      Math.max(
        0,
        items.findIndex((i) => i.value === opportunityStageId),
      ),
    [opportunityStageId, items],
  )

  const handleStatusClick = useCallback((value: number | string | null) => {
    setValues((model) => ({
      ...model,
      opportunityStageClosedLostIdTemp: '',
      opportunityStageName: '',
      opportunityStageId: value ? value?.toString() : '',
    }))
    setIsSelectedCloseWon(false)
    onClosedWonChange(false, false)
  }, [])

  const handleClosedWonMenuItemClick = useCallback(() => {
    setActiveSegmentIndex(null)
    setIsSelectedCloseWon(true)
    onClosedWonChange(true, false)
    setValues((model) => ({
      ...model,
      opportunityStageId: closedWonStageId?.toString(),
      opportunityStageClosedLostIdTemp: '',
      opportunityStageName: closedWonOpportunityStageName,
    }))
    setAnchorEl(null)
  }, [closedWonStageId])

  const handleClosedLostMenuItemClick = useCallback(() => {
    setActiveSegmentIndex(null)
    setIsSelectedCloseWon(false)
    onClosedWonChange(false, true)
    setValues((model) => ({
      ...model,
      opportunityStageId: '',
      opportunityStageClosedLostIdTemp: closedLostStageId?.toString(),
      opportunityStageName: closedLostOpportunityStageName,
    }))
    setAnchorEl(null)
  }, [closedLostStageId])

  return (
    <>
      <Typography variant="subtitle3">Opportunity Stage*</Typography>
      <Box sx={{ mt: 1 }}>
        <ProgressWidget
          colors={colors}
          disabled={false}
          height={32}
          activeSegmentIndex={activeSegmentIndex}
          onActiveIndexChange={setActiveSegmentIndex}
          segmentMinWidth={220}
          segmentSpacing={6}
          transitionTime={0.5}
          items={items}
          onChange={handleStatusClick}
          scrollTo={lastCompleteIndex}
          isTidalEnabled={isTidalEnabled}
        />
        <ClosedWonLostMenu
          open={wonLostMenuOpen}
          onClose={() => setAnchorEl(null)}
          onClosedLostClick={handleClosedLostMenuItemClick}
          onClosedWonClick={handleClosedWonMenuItemClick}
          opportunityId={-1}
          anchorEl={anchorEl}
          disableCloseWon={!isCloseableDetails?.closeable}
          notCloseableMessage={notCloseableMessage}
        />
        {opportunityStageClosedLostIdTemp != closedLostStageId?.toString() &&
          showError && (
            <FormHelperText sx={{ ml: 1 }} error>
              {helperText}
            </FormHelperText>
          )}
        {isSelectedCloseWon && (
          <Alert sx={{ mt: 1 }} severity="info">
            <AlertTitle sx={{ fontWeight: 600 }}>
              Opportunity Stage Closed Won
            </AlertTitle>
            <Typography variant="body2">
              This opportunity will be submitted to the closed/won queue upon
              saving.
            </Typography>
          </Alert>
        )}
      </Box>
    </>
  )
}
